import { Injectable } from '@angular/core';
import { MeasureColumnVisibility } from '../components/measures/measure-column-toggle/measure-column-toggle.component';
import { isEndBeforeStart } from '../utils/date-comparison.utils';

const DATE_RANGE_KEY = 'date_range';
const GRAPH_AUTO_REFRESH_KEY = 'graph_auto_refresh';
const GRAPH_LEGEND_COLLAPSED_KEY = 'graph_legend_collapsed';

export type MeasureColumnScope = 'automatic' | 'campaign' | 'analysisPost';

const MEASURE_COLUMN_KEYS: Record<MeasureColumnScope, string> = {
  automatic: 'automatic_measure_columns',
  campaign: 'campaign_measure_columns',
  analysisPost: 'analysis_post_measure_columns',
};

@Injectable({ providedIn: 'root' })
export class ViewPreferencesService {
  saveDateRange(start: Date | null, end: Date | null): void {
    if (!start || !end) {
      localStorage.removeItem(DATE_RANGE_KEY);
      return;
    }
    // An inverted window is a transient picker error, not a preference worth restoring elsewhere.
    if (isEndBeforeStart(start, end)) return;
    localStorage.setItem(DATE_RANGE_KEY, JSON.stringify({ start: start.toISOString(), end: end.toISOString() }));
  }

  getDateRange(): { startDate: Date | null; endDate: Date | null } {
    try {
      const stored = localStorage.getItem(DATE_RANGE_KEY);
      if (!stored) return { startDate: null, endDate: null };

      const parsed: unknown = JSON.parse(stored);
      const isStored = (v: unknown): v is { start: string; end: string } =>
        typeof v === 'object' &&
        v !== null &&
        typeof (v as { start: unknown }).start === 'string' &&
        typeof (v as { end: unknown }).end === 'string';

      if (!isStored(parsed)) {
        localStorage.removeItem(DATE_RANGE_KEY);
        return { startDate: null, endDate: null };
      }

      const startDate = new Date(parsed.start);
      const endDate = new Date(parsed.end);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        localStorage.removeItem(DATE_RANGE_KEY);
        return { startDate: null, endDate: null };
      }

      return { startDate, endDate };
    } catch {
      localStorage.removeItem(DATE_RANGE_KEY);
      return { startDate: null, endDate: null };
    }
  }

  saveAutoRefresh(enabled: boolean): void {
    localStorage.setItem(GRAPH_AUTO_REFRESH_KEY, JSON.stringify(enabled));
  }

  getAutoRefresh(): boolean {
    try {
      const stored = localStorage.getItem(GRAPH_AUTO_REFRESH_KEY);
      return stored !== null && (JSON.parse(stored) as unknown) === true;
    } catch {
      return false;
    }
  }

  saveLegendCollapsed(collapsed: boolean): void {
    localStorage.setItem(GRAPH_LEGEND_COLLAPSED_KEY, JSON.stringify(collapsed));
  }

  getLegendCollapsed(): boolean {
    try {
      const stored = localStorage.getItem(GRAPH_LEGEND_COLLAPSED_KEY);
      return stored !== null && (JSON.parse(stored) as unknown) === true;
    } catch {
      return false;
    }
  }

  saveMeasureColumns(scope: MeasureColumnScope, columns: MeasureColumnVisibility): void {
    localStorage.setItem(MEASURE_COLUMN_KEYS[scope], JSON.stringify(columns));
  }

  getMeasureColumns(scope: MeasureColumnScope): MeasureColumnVisibility | null {
    const key = MEASURE_COLUMN_KEYS[scope];
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      const parsed: unknown = JSON.parse(stored);
      if (typeof parsed !== 'object' || parsed === null) {
        localStorage.removeItem(key);
        return null;
      }
      const shape = parsed as MeasureColumnVisibility;
      const baseValid = typeof shape.status === 'boolean' && typeof shape.verification === 'boolean';
      const scopedValid = scope === 'analysisPost' ? typeof shape.parameters === 'boolean' : typeof shape.calculated === 'boolean';
      if (baseValid && scopedValid) {
        return shape;
      }
      localStorage.removeItem(key);
      return null;
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  }
}
